"use client";

import React from "react";
import { MapPin, Globe } from "lucide-react";
import MaxWidthWrapper from "@/components/common/layout/MaxWidthWrapper";
import { useGetAllAddresses } from "@/apis/cart/Queries";
import { useNavigate } from 'react-router'

const AddressAndLanguage: React.FC = () => {
  const { data: GetAllAddress } = useGetAllAddresses();
  const Navigate = useNavigate()
  const handleAddAddress = () => {
    Navigate('/address')
  }

  // Load selected address from localStorage on mount
  const [selectedAddressId, setSelectedAddressId] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedAddressId') || "";
    }
    return "";
  });
  const [selectedLang, setSelectedLang] = React.useState("en");

  // Save selected address to localStorage whenever it changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      if (selectedAddressId) {
        localStorage.setItem('selectedAddressId', selectedAddressId);
      } else {
        localStorage.removeItem('selectedAddressId');
      }
    }
  }, [selectedAddressId]);

  // Set default address if no address is selected but addresses exist
  React.useEffect(() => {
    if (!selectedAddressId && GetAllAddress && GetAllAddress.length > 0) {
      const defaultAddress = GetAllAddress.find((addr: any) => addr.isDefault) || GetAllAddress[0];
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress._id);
      }
    }
  }, [GetAllAddress, selectedAddressId]);

  const selectedAddress = GetAllAddress?.find(
    (addr) => addr._id === selectedAddressId
  );
  const hasNoAddresses = !GetAllAddress || GetAllAddress.length === 0;

  const changeLanguage = (lang: string) => {
    setSelectedLang(lang);
    const selectField = document.querySelector(
      ".goog-te-combo"
    ) as HTMLSelectElement;
    if (selectField) {
      selectField.value = lang;
      selectField.dispatchEvent(new Event("change"));
    }
  };

  return (
    <div className="bg-white md:bg-gray-50 relative z-50">
      <MaxWidthWrapper>
        <div className="flex flex-col md:flex-row gap-5 md:items-center md:justify-between w-full py-1">
          {/* Address Selector */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 flex items-center justify-center rounded border bg-gray-100">
              <MapPin className="w-4 h-4 text-gray-500" />
            </div>

            <div className="flex flex-col">
              {hasNoAddresses ? (
                <div className="flex flex-col space-y-1">
                  <p className="text-gray-700 text-[12px] font-medium">
                    No addresses available
                  </p>
                  <button
                    onClick={handleAddAddress}
                    className="text-blue-500 text-xs underline"
                  >
                    Add address
                  </button>
                </div>
              ) : (
                <div className="flex flex-col">
                  {selectedAddress ? (
                    <p className="text-gray-700 text-[12px] font-medium truncate max-w-[200px] sm:max-w-[300px] md:max-w-[400px]">
                      <b>
                        {selectedAddress.city ||
                          selectedAddress.town ||
                          selectedAddress.district ||
                          "Home"}
                      </b>
                    </p>
                  ) : (
                    <p className="text-gray-700 text-[12px] font-medium">
                      Select an address
                    </p>
                  )}

                  <select
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                    className="text-red-500 text-[12px] bg-transparent border-none outline-none p-0 cursor-pointer"
                  >
                    <option value="" disabled>
                      Change location
                    </option>
                    {GetAllAddress?.map((addr: any) => (
                      <option key={addr._id} value={addr._id}>
                        {addr.fullName} ({addr.label})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Language Selector */}
          <div className="flex items-center space-x-1">
            <Globe className="w-4 h-4 text-gray-500" />
            <select
              value={selectedLang}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-transparent border-none outline-none text-gray-600 text-md font-medium cursor-pointer"
            >
              <option className="notranslate" value="en">EN</option>
              <option className="notranslate" value="ar">AR</option>
              <option className="notranslate" value="hi">HI</option>

            </select>
          </div>
        </div>
      </MaxWidthWrapper>
    </div>
  );
};

export default AddressAndLanguage;